import { Shield } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { getScoreTierLabel, getScoreTip, type ScoreTier } from "@/hooks/useCredit";
import { useScoreChanges } from "@/hooks/useScoreChanges";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { formatDistanceToNow } from "date-fns";
import { cn } from "@/lib/utils";

interface CreditScoreWidgetProps {
  score: number;
  tier: ScoreTier;
  isLocked: boolean;
}

const TIER_COLORS: Record<ScoreTier, string> = {
  poor: "text-destructive",
  fair: "text-amber-500",
  good: "text-primary",
  excellent: "text-emerald-500",
};

const TIER_BAR_COLORS: Record<ScoreTier, string> = {
  poor: "[&>div]:bg-destructive",
  fair: "[&>div]:bg-amber-500",
  good: "[&>div]:bg-primary",
  excellent: "[&>div]:bg-emerald-500",
};

const CreditScoreWidget = ({ score, tier, isLocked }: CreditScoreWidgetProps) => {
  const { changes } = useScoreChanges("credit", 5);
  // Map 300-850 to 0-100 for progress bar
  const pct = Math.round(((score - 300) / 550) * 100);

  if (isLocked) {
    return (
      <div className="glass-card p-5 space-y-2 opacity-60">
        <div className="flex items-center gap-2">
          <Shield className="w-4 h-4 text-muted-foreground" />
          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Credit Score</span>
        </div>
        <p className="font-heading font-bold text-2xl text-muted-foreground">No Score Yet</p>
        <p className="text-xs text-muted-foreground">Unlock your Credit Account to start building credit.</p>
      </div>
    );
  }

  return (
    <HoverCard openDelay={120} closeDelay={80}>
      <HoverCardTrigger asChild>
        <div className="glass-card p-5 space-y-3 cursor-help">
          <div className="flex items-center gap-2">
            <Shield className="w-4 h-4 text-secondary" />
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Credit Score</span>
          </div>
          <div className="flex items-end gap-2">
            <p className={`font-heading font-bold text-3xl ${TIER_COLORS[tier]}`}>{score}</p>
            <span className={`text-sm font-semibold pb-0.5 ${TIER_COLORS[tier]}`}>
              {getScoreTierLabel(tier)}
            </span>
          </div>
          <Progress value={pct} className={`h-2 ${TIER_BAR_COLORS[tier]}`} />
          <div className="flex items-center justify-between text-[10px] text-muted-foreground">
            <span>300</span><span>550</span><span>700</span><span>850</span>
          </div>
          <p className="text-xs text-muted-foreground/80 italic">{getScoreTip(tier)}</p>
        </div>
      </HoverCardTrigger>
      <HoverCardContent side="bottom" align="start" className="w-80 p-3">
        <p className="font-semibold text-xs mb-2">Why your score moved</p>
        {changes.length > 0 ? (
          <div className="space-y-1.5">
            {changes.map(c => {
              const delta = Number(c.delta ?? 0);
              const sign = delta > 0 ? "+" : "";
              const cls = delta > 0 ? "text-emerald-500" : delta < 0 ? "text-destructive" : "text-muted-foreground";
              return (
                <div key={c.id} className="flex items-start justify-between gap-2 text-[10px]">
                  <div className="flex-1 min-w-0">
                    <p className="text-foreground/90">{c.reason}</p>
                    <p className="text-muted-foreground/60">
                      {formatDistanceToNow(new Date(c.created_at), { addSuffix: true })}
                    </p>
                  </div>
                  <span className={cn("font-mono font-semibold shrink-0", cls)}>{sign}{delta}</span>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-[10px] text-muted-foreground italic">No score changes yet — make a payment to see how habits move your score.</p>
        )}
      </HoverCardContent>
    </HoverCard>
  );
};

export default CreditScoreWidget;
